import Vue from "vue";
import VueRouter, { Route, RouteConfig } from "vue-router";
import store from "../store";
import { ApplicationRole, Role } from "@/services/models/Role";
import Home from "../views/Home.vue";
import TheOrganizationList from "../views/organizations/TheOrganizationList.vue";
import TheOrganizationForm from "../views/organizations/TheOrganizationDetails.vue";
import ThePersonDetails from "../views/people/ThePersonDetails.vue";
import ThePersonList from "../views/people/ThePersonList.vue";
import TheGeneralCodeAddForm from "../views/generalCodes/TheGeneralCodeAddForm.vue";
import TheGeneralCodeEditForm from "../views/generalCodes/TheGeneralCodeEditForm.vue";
import TheGeneralCodeList from "../views/generalCodes/TheGeneralCodeList.vue";
import TheAdministrativeLocationList from "../views/administrativeLocations/TheAdministrativeLocationList.vue";
import TheAdministrativeLocationAddForm from "../views/administrativeLocations/TheAdministrativeLocationAddForm.vue";
import TheAdministrativeLocationEditForm from "../views/administrativeLocations/TheAdministrativeLocationEditForm.vue";
import TheDataDashboardList from "../views/dataDashboards/TheDataDashboardList.vue";
import TheBuildingList from "../views/buildings/TheBuildingList.vue";
import TheBuildingAddForm from "../views/buildings/TheBuildingAddForm.vue";
import TheBuildingEditForm from "../views/buildings/TheBuildingEditForm.vue";
import TheFacilitiesKeysList from "../views/facilitiesKeys/TheFacilitiesKeysList.vue";
import TheFacilitiesKeyDetails from "../views/facilitiesKeys/TheFacilitiesKeyDetails.vue";
import TheFacilitiesKeyAddForm from "../views/facilitiesKeys/TheFacilitiesKeyAddForm.vue";
import TheFacilitiesKeyCopyAddForm from "../views/facilitiesKeys/TheFacilitiesKeyCopyAddForm.vue";
import TheFacilitiesKeyCopyEditForm from "../views/facilitiesKeys/TheFacilitiesKeyCopyEditForm.vue";
import TheFacilitiesKeyLockAddForm from "../views/facilitiesKeys/TheFacilitiesKeyLockAddForm.vue";
import TheFacilitiesKeyLockEditForm from "../views/facilitiesKeys/TheFacilitiesKeyLockEditForm.vue";
import TheFacilitiesLocksList from "../views/facilitiesLocks/TheFacilitiesLocksList.vue";
import TheFacilitiesLockAddForm from "../views/facilitiesLocks/TheFacilitiesLockAddForm.vue";
import TheFacilitiesLockEditForm from "../views/facilitiesLocks/TheFacilitiesLockEditForm.vue";
import TheInterdepartmentalTransfersList from "../views/interdepartmentalTransfers/TheInterdepartmentalTransfersList.vue";
import TheIDTAddForm from "../views/interdepartmentalTransfers/TheIDTAddForm.vue";
import TheIDTEditForm from "../views/interdepartmentalTransfers/TheIDTEditForm.vue";
import TheVisaList from "../views/visas/TheVisaList.vue";
import TheVisaDetails from "../views/visas/TheVisaDetails.vue";
import TheComputerAssetList from "../views/computers/TheComputerAssetList.vue";
import TheComputerAssetAddForm from "../views/computers/TheComputerAssetAddForm.vue";
import TheComputerAssetEditForm from "../views/computers/TheComputerAssetEditForm.vue";
import TheComputerLeaseList from "../views/computers/TheComputerLeaseList.vue";
import TheComputerLeaseAddForm from "../views/computers/TheComputerLeaseAddForm.vue";
import TheComputerLeaseEditForm from "../views/computers/TheComputerLeaseEditForm.vue";
import TheOracleModuleRoleList from "../views/oracle/TheOracleModuleRoleList.vue";
import TheOracleModuleRoleAddForm from "../views/oracle/TheOracleModuleRoleAddForm.vue";
import TheOracleAccountList from "../views/oracle/TheOracleAccountList.vue";
import TheOracleAccountDetails from "../views/oracle/TheOracleAccountDetails.vue";
import ThePayPeriodList from "../views/academicIndirectCost/ThePayPeriodList.vue";
import ThePayPeriod from "../views/academicIndirectCost/ThePayPeriod.vue";
import TheAdLoc from "../views/academicIndirectCost/TheAdLoc.vue";
import Error from "../views/Error.vue";
import TheAzureBillingList from "@/views/azureBilling/TheAzureBillingList.vue";
import TheAzureBillingSubscriptionList from "@/views/azureBilling/TheAzureBillingSubscriptionList.vue";
import TheAzureBillingSubscriptionDetails from "@/views/azureBilling/TheAzureBillingSubscriptionDetails.vue";
import TheUserSettings from "@/views/settings/TheUserSettings.vue"
import TheTitlesList from "@/views/titles/TheTitlesList.vue"

Vue.use(VueRouter);

/**
 * @description Checks to see if the current user has a role in one of the specified params. If it does then the user is
 * navigated to that route, if not the routing is aborted.
 * @return {boolean}
 */
function authorizeRoute(
  to: Route,
  from: Route,
  next: Function,
  allowDelegates: boolean,
  ...roles: ApplicationRole[]
) {
  // Admin role has full access
  const isAdministrator: boolean = store.getters.user
    ? store.getters.user.roles.find(
      (r: Role) => r.id.toUpperCase() == ApplicationRole.Administrator
    )
    : false;
  const hasRole: boolean = !store.getters.user
    ? false
    : store.getters.user.roles.some(
      (r: Role) => roles.indexOf(r.id.toUpperCase() as ApplicationRole) >= 0
        && (allowDelegates || !r.isDelegated));

  if (isAdministrator || hasRole) {
    // Proceed to the next route
    next();
  } else {
    // the user didn't have permissions
    next(false);
  }
}

const routes: Array<RouteConfig> = [
  {
    path: "/",
    name: "Home",
    component: Home,
  },
  {
    path: "/Error",
    name: "Error",
    component: Error,
    props: true,
  },
  {
    path: "/Organizations",
    name: "Organizations",
    component: TheOrganizationList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities,
        ApplicationRole.NetworkAdministrator,
        ApplicationRole.Business,
        ApplicationRole.TTIWebsiteAdministrator
      );
    },
  },
  {
    path: "/Organizations/:id",
    name: "OrganizationDetails",
    props: true,
    component: TheOrganizationForm,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities,
        ApplicationRole.NetworkAdministrator,
        ApplicationRole.Business,
        ApplicationRole.TTIWebsiteAdministrator
      );
    },
  },
  {
    path: "/People",
    name: "People",
    component: ThePersonList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities,
        ApplicationRole.NetworkAdministrator,
        ApplicationRole.Business,
        ApplicationRole.TTIWebsiteAdministrator
      );
    },
  },
  {
    path: "/People/:id",
    name: "PersonDetails",
    component: ThePersonDetails,
    props: true,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities,
        ApplicationRole.NetworkAdministrator,
        ApplicationRole.Business,
        ApplicationRole.TTIWebsiteAdministrator
      );
    },
  },
  {
    path: "/GeneralCodes",
    name: "GeneralCodes",
    component: TheGeneralCodeList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator);
    },
  },
  {
    path: "/GeneralCodes/Add",
    name: "AddGeneralCode",
    component: TheGeneralCodeAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator);
    },
  },
  {
    path: "/GeneralCodes/:id",
    name: "GeneralCodeDetails",
    props: true,
    component: TheGeneralCodeEditForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator);
    },
  },
  {
    path: "/AdministrativeLocations",
    name: "AdministrativeLocations",
    component: TheAdministrativeLocationList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.HR
      );
    },
  },
  {
    path: "/AdministrativeLocations/Add",
    name: "AddAdministrativeLocation",
    component: TheAdministrativeLocationAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.HR
      );
    },
  },
  {
    path: "/AdministrativeLocations/:id",
    name: "AdministrativeLocationDetails",
    props: true,
    component: TheAdministrativeLocationEditForm,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.HR
      );
    },
  },
  {
    path: "/NIS/DataDashboards",
    name: "DataDashboards",
    component: TheDataDashboardList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
      );
    }
  },
  {
    path: "/Facilities/Offices",
    name: "Buildings",
    component: TheBuildingList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities
      );
    },
  },
  {
    path: "/Facilities/Offices/Add",
    name: "AddBuilding",
    component: TheBuildingAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities
      );
    },
  },
  {
    path: "/Facilities/Offices/:id",
    name: "BuildingDetails",
    props: true,
    component: TheBuildingEditForm,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities
      );
    },
  },
  {
    path: "/Facilities/Keys",
    name: "Keys",
    component: TheFacilitiesKeysList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.Facilities
      );
    }
  },
  {
    path: '/Facilities/Keys/:id',
    name: 'KeyDetails',
    component: TheFacilitiesKeyDetails,
    props: true,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Keys/Add',
    name: 'AddKey',
    component: TheFacilitiesKeyAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Keys/:facilitiesKeyId/AddCopies',
    name: 'AddKeyCopies',
    component: TheFacilitiesKeyCopyAddForm,
    props: true,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Keys/EditCopy/:id',
    name: 'EditKeyCopy',
    component: TheFacilitiesKeyCopyEditForm,
    props: true,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Keys/:facilitiesKeyId/AddLocks',
    name: 'AddKeyLocks',
    component: TheFacilitiesKeyLockAddForm,
    props: true,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Keys/EditLock/:id',
    name: 'EditKeyLock',
    component: TheFacilitiesKeyLockEditForm,
    props: true,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Locks',
    name: 'Locks',
    component: TheFacilitiesLocksList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Locks/Add',
    name: 'AddLock',
    component: TheFacilitiesLockAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/Facilities/Locks/Edit/:id',
    name: 'EditLock',
    component: TheFacilitiesLockEditForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.Facilities)
    }
  },
  {
    path: '/HumanResources/VISAs',
    name: 'VISAs',
    component: TheVisaList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.HR, ApplicationRole.Management)
    }
  },
  {
    path: '/HumanResources/VISAs/:id',
    name: 'VisaDetails',
    props: true,
    component: TheVisaDetails,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator, ApplicationRole.HR)
    }
  },
  {
    path: "/FinancialServices/IDTs",
    name: "IDTs",
    component: TheInterdepartmentalTransfersList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.Business
      );
    }
  },
  {
    path: '/FinancialServices/IDTs/:type/Add',
    name: 'AddIDT',
    props: true,
    component: TheIDTAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.Business)
    }
  },
  {
    path: "/FinancialServices/IDTs/:type/Edit/:sl-:sa",
    name: "EditIDT",
    props: true,
    component: TheIDTEditForm,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.Business
      );
    },
  },
  {
    path: '/FinancialServices/Computers/Assets',
    name: 'ComputerAssets',
    component: TheComputerAssetList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.Business)
    }
  },
  {
    path: '/FinancialServices/Computers/Assets/Add',
    name: 'AddComputerAsset',
    component: TheComputerAssetAddForm,
    props: true,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.Business)
    }
  },
  {
    path: '/FinancialServices/Computers/Assets/Edit/:id',
    name: 'EditComputerAsset',
    props: true,
    component: TheComputerAssetEditForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.Business)
    }
  },
  {
    path: '/FinancialServices/Computers/Leases',
    name: 'ComputerLeases',
    component: TheComputerLeaseList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.Business)
    }
  },
  {
    path: '/FinancialServices/Computers/Leases/Add',
    name: 'AddComputerLease',
    component: TheComputerLeaseAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.Business)
    }
  },
  {
    path: '/FinancialServices/Computers/Leases/Edit/:id',
    name: 'EditComputerLease',
    props: true,
    component: TheComputerLeaseEditForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.Business)
    }
  },
  {
    path: '/Research/AcademicIndirectCost/PayPeriods',
    name: 'PayPeriods',
    component: ThePayPeriodList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.FinanceAdministrator)
    }
  },
  {
    path: '/Research/AcademicIndirectCost/PayPeriods/:payPeriodStartDateDisplay',
    name: 'PayPeriod',
    props: true,
    component: ThePayPeriod,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.FinanceAdministrator)
    }
  },
  {
    path: '/Research/AcademicIndirectCost/PayPeriods/:payPeriodStartDateDisplay/AdLocs/:adLoc',
    name: 'AdLoc',
    props: true,
    component: TheAdLoc,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.FinanceAdministrator)
    }
  },
  {
    path: "/Oracle/ModuleRoles",
    name: "OracleModuleRoles",
    component: TheOracleModuleRoleList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator);
    },
  },
  {
    path: "/Oracle/ModuleRoles/Add",
    name: "AddOracleModuleRole",
    component: TheOracleModuleRoleAddForm,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator);
    },
  },
  {
    path: "/Oracle/Accounts",
    name: "OracleAccounts",
    component: TheOracleAccountList,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator);
    },
  },
  {
    path: "/Oracle/Accounts/:id",
    name: "OracleAccountDetails",
    props: true,
    component: TheOracleAccountDetails,
    beforeEnter(to, from, next) {
      authorizeRoute(to, from, next, true, ApplicationRole.DatabaseAdministrator);
    },
  },
  {
    path: "/AzureBilling",
    name: "AzureBilling",
    component: TheAzureBillingList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.NetworkAdministrator
      );
    },
  },
  {
    path: "/AzureBilling/:billingId/Subscriptions",
    name: "AzureBillingSubscriptions",
    component: TheAzureBillingSubscriptionList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.NetworkAdministrator
      );
    },
  },
  {
    path: "/AzureBilling/:billingId/Subscriptions/:subscriptionId",
    name: "AzureBillingSubscriptionDetails",
    component: TheAzureBillingSubscriptionDetails,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        true,
        ApplicationRole.DatabaseAdministrator,
        ApplicationRole.NetworkAdministrator
      );
    },
  },
  {
    path: "/Settings",
    name: "Settings",
    component: TheUserSettings,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        false,
        ApplicationRole.Management
      )
    }
  },
  {
    path: "/Titles",
    name: "Titles",
    component: TheTitlesList,
    beforeEnter(to, from, next) {
      authorizeRoute(
        to,
        from,
        next,
        false,
        ApplicationRole.DatabaseAdministrator
      )
    }
  }
];

const router = new VueRouter({
  routes,
  scrollBehavior(to, from, savedPosition) {
    // return desired position
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
});

export default router
